<template>
  <v-form ref="form" class="form">
    <h2>{{ $t("projects.general-settings") }}</h2>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-text-field
          v-model="project.name"
          :label="$t('projects.name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('name')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="project.timezone"
          :items="timezones"
          :label="$t('profile.timezone')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('timezone')]
          "
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-textarea
          v-model="project.address"
          :label="$t('projects.address')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('address')]"
          :readonly="readonly"
        ></v-textarea>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-text-field
          v-model="project.address_zip"
          :label="$t('projects.address-zip')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('address_zip')]"
          :readonly="readonly"
        ></v-text-field>
        <v-text-field
          v-model="project.address_town"
          :label="$t('projects.address-town')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('address_town')]"
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="project.number_format"
          :items="locales"
          item-value="code"
          item-text="name"
          :label="$t('projects.number-format')"
          :rules="[rules.required, backEndValidation('number_format')]"
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="project.date_format"
          :items="locales"
          item-value="code"
          item-text="name"
          :label="$t('projects.date-format')"
          :rules="[rules.required, backEndValidation('date_format')]"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <h2>{{ $t("projects.invoice-information") }}</h2>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-textarea
          v-model="project.invoice_address"
          :label="$t('projects.invoice-address')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [
                  rules.requiredWithEmptyString,
                  backEndValidation('invoice_address')
                ]
          "
          :readonly="readonly"
        ></v-textarea>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-textarea
          v-model="project.invoice_detail"
          :label="$t('projects.invoice-detail')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('invoice_detail')]"
          :readonly="readonly"
        ></v-textarea>
      </v-col>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="project.invoice_language"
          :items="languages"
          item-value="code"
          item-text="name"
          :label="$t('projects.invoice-language')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('invoice_language')]
          "
          :readonly="readonly"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <h2>{{ $t("projects.other-settings") }}</h2>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-autocomplete
          v-model="project.currency"
          :items="currencies"
          item-value="code"
          item-text="name"
          :label="$t('projects.currency')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [maxLength, backEndValidation('currency')]
          "
          :readonly="readonly"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-btn
      color="primary mr-3"
      v-on:click="save()"
      :loading="loading"
      :disabled="readonly"
    >
      <v-icon left>{{ $props.button_icon }}</v-icon>
      {{ $t(`projects.${$props.button_title}`) }}
    </v-btn>
  </v-form>
</template>

<script>
import { rules } from "../mixins/rules";
import moment from "moment";
export default {
  name: "FormProject",
  mixins: [rules],

  data: () => ({
    loading: false,
    timezones: moment.tz.names()
  }),
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    readonly: Boolean,
    project: {
      default: function() {
        return {
          invoice_address: ""
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    organization() {
      return this.$store.getters["organizationStore/getItem"];
    },
    locales: {
      get() {
        return this.$store.getters["localizationStore/localeStore/getList"];
      }
    },
    currencies: {
      get() {
        return [
          ...(this.$store.getters["localizationStore/currencyStore/getList"] ||
            [])
        ].map(i => ({ ...i, name: `${i.name} (${i.code})` }));
      }
    },
    languages: {
      get() {
        return this.locales.filter(i => i.language);
      }
    }
  },
  methods: {
    save() {
      this.clearBackEndErrors();
      if (!this.readonly && this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.project
        };
        if (!item.id) {
          item.organization_id = this.organization.id;
        }
        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: item => {
              if (item) {
                this.$store.commit("projectMeteringStore/meterUnitStore/set", {
                  item
                });
                this.$router.push({
                  name: "project",
                  params: {
                    id: item.id
                  }
                });
              }
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    maxLength(value) {
      return (
        (value || "").length <= 6 ||
        this.$t("projects.rule-max-currency-length")
      );
    }
  },
  mounted() {
    let locale = this.$i18n && this.$i18n.locale;
    this.$store.dispatch("localizationStore/currencyStore/getList", {
      params: {
        limit: -1,
        sort: "name",
        direction: "asc",
        locale
      }
    });
  },
  watch: {
    "project.invoice_address": function(newVal) {
      if (!newVal) {
        this.project.invoice_address = null;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.factor {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .btn {
    flex: 0 0 36px;
    margin-left: 15px;
  }
  .name {
    flex: 1 1;
  }
  .factor {
    margin-left: 15px;
    flex: 0 0 100px;
  }
}
</style>
